import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Contrast } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { ActionButtons, Label } from '../OperationFormControls';
import { menuProps } from '../selectMenuProps';
import { useContourLayerViewUpdate } from '~/hooks/structure-set/useContourLayerViewUpdate';
import {
    COUCH_MODELS,
    CouchModelId,
    CouchVariant,
    requireCouchModel,
    requireCouchVariant,
} from '~/domain/couchtop/couchLibrary';
import { couchCoverage } from '~/domain/couchtop/coverage';
import { findBodyRoi } from '~/domain/couchtop/findBodyRoi';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useAcquiredPrimarySliceShown } from '~/hooks/environment/useEnvironmentViews';
import { useParkedContouringTool } from '~/components/toolbar/shared/useParkedContouringTool';
import {
    CouchTopFinding,
    CouchTopMethod,
    findCouchTop,
} from '~/domain/couchtop/findCouchTop';
import {
    imageRowWorldAxis,
    couchTopWorldCoord,
} from '~/domain/couchtop/geometry';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { requireWindowingPreset } from '~/domain/patient/windowLevel';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import {
    useCouchTopRow,
    useCouchTopRowReset,
    useSetCouchTopRow,
} from '~/hooks/useCouchTopRow';
import { useReviewWindowLevel } from '~/hooks/useReviewWindowLevel';
import { couchModelPreviewSelectionState } from '~/state/atoms/couchTopAtoms';
import { formatMmAsCm } from '~/utils/units';
import CouchStructureRow from './CouchStructureRow';
import ExistingSupportStructuresNotice from './ExistingSupportStructuresNotice';
import { useSupportRois } from './useSupportRois';
import { useCouchStructureRows } from './useCouchStructureRows';
import { useCouchModelApplication } from './useCouchModelApplication';
import { structureSdfOf } from '~/domain/structures/structureSdf';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

/** The window/level preset this panel reviews the couch under — a
 *  registry entry (`WINDOWING_PRESETS`, `windowLevel.ts`), never
 *  hardcoded ww/wc here (see {@link useReviewWindowLevel}). */
const REVIEW_PRESET_NAME = 'Couch';

/**
 * Where placing the line seeds the couch top: 75% of the way down the
 * image. A plain heuristic, not a measurement — the couch sits below the
 * patient, so the lower quarter boundary lands the line near it on a
 * typical planning CT and the user drags it the rest of the way.
 */
const INITIAL_PLACEMENT_HEIGHT_FRACTION = 0.75;

/** How the couch-top readout names where its line came from. */
const COUCH_TOP_METHOD_MESSAGE_ID: Record<CouchTopMethod, string> = {
    body: 'couch.foundFromBody',
    image: 'couch.foundFromImage',
    default: 'couch.foundFromNeither',
};

/** Patient-frame axis letters, indexed by {@link imageRowWorldAxis}'s
 *  return. Untranslated: the DICOM patient axes are named X/Y/Z in every
 *  locale, same as the viewport's coordinate readout. */
const AXIS_LETTERS = ['X', 'Y', 'Z'] as const;

/**
 * The variant's label message id. Every variant of a multi-variant model
 * carries one — the domain type leaves `labelId` optional only for the
 * single-variant models whose Select is never rendered. Throws rather
 * than falling back to the raw library id: a snake_case identifier is not
 * a label a clinician can act on.
 */
function requireVariantLabelId(variant: CouchVariant): string {
    if (variant.labelId === undefined) {
        throw new Error(`Couch variant '${variant.id}' has no label`);
    }
    return variant.labelId;
}

interface CouchOperationContentProps {
    /** Closes the panel, called on a successful Apply exactly as the
     *  margin/ring/boolean/body-contour siblings do. */
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

/**
 * Couch structure panel — place the couch-top line, review it under a
 * window that makes the couch visible, pick a library couch model and
 * variant, name and colour its structures, and add them to the structure
 * set. The ghost preview follows from having both a line and a model, so
 * the placement is always visible before it is committed.
 *
 * Does NOT use `OperationContentShell`: its guards require a selected
 * ROI, and this panel's input is the image, exactly the reason
 * `BodyContourOperationContent` bypasses it too — inputs are read
 * from the resolved edit target below.
 *
 * Does not itself write any structure — placing the model
 * (`placeCouchModel`) and writing its contours (`writeRoiContours`,
 * `useGetOrCreateDestination`) is `useCouchModelApplication`'s job; this
 * panel only collects the selection and forwards it to Apply.
 */
export default function CouchOperationContent({
    onClose,
    onPreventCloseChange,
}: CouchOperationContentProps) {
    const intl = useIntl();
    const { environment: activeEnvironment, structureSet: readModel } =
        useActiveEditTarget();
    // The resolver yields `undefined`; the consumers below take `null`.
    // Normalise once at the boundary.
    const environment = activeEnvironment ?? null;
    const image = activeEnvironment?.image?.parent;
    // The couch-top line is a draggable control over the CT for as long as
    // the panel is open: a press that misses it by a few pixels must not
    // land as a brush stroke, and a contouring cursor must not repaint the
    // shared overlay the couch preview is drawn on — clearing it on every
    // mousemove, until the next scroll redraws it.
    useParkedContouringTool(true);
    const couchTopRow = useCouchTopRow(image?.seriesUid);
    const setCouchTopRow = useSetCouchTopRow();
    const setPreviewSelection = useSetRecoilState(
        couchModelPreviewSelectionState
    );
    const resetCouchTopRows = useCouchTopRowReset();

    // Apply extrudes the cross-section over every slice, so it refuses a
    // volume that is not all there; the panel stays open because "still
    // loading" resolves on its own, the same as the threshold-segmentation
    // panel. Placement and preview are left alone by this gate: neither
    // reads a pixel nor writes a contour, and the user must be able to see
    // where the couch would land while the image finishes arriving.
    const imageGate = useImageCompletenessGate(image);

    // The line and its ghost draw only on a tile of the edit target that
    // shows an acquired slice of its primary image. While no tile does,
    // neither placing the line nor Apply is offered: the user could not
    // see the couch they would place. The panel stays open, because
    // showing such a tile again brings both back.
    const acquiredSliceShown = useAcquiredPrimarySliceShown(activeEnvironment);
    const acquisitionPlaneReason = acquiredSliceShown
        ? undefined
        : intl.formatMessage({ id: 'tool.disabled.acquisitionPlaneNotShown' });

    const updateView = useContourLayerViewUpdate(environment);

    // ONE hook instance for the whole panel — the display window is a
    // property of the SERIES, matching every other caller of this shared
    // hook. The hook windows the image this panel acts on.
    const reviewWindowLevel = useReviewWindowLevel(REVIEW_PRESET_NAME, image, {
        restoreOnUnmount: true,
    });

    // The couch is invisible at a diagnostic preset, and looking at it is
    // the whole reason this panel is open — so the review window comes on
    // with it rather than waiting for a press the user would always make,
    // and goes back off when the panel closes (`restoreOnUnmount` above):
    // having taken the display without being asked, the tool gives it back
    // the same way rather than leaving the couch window over the patient.
    //
    // `activate` is idempotent and keeps its identity across captures, so
    // this neither double-applies under StrictMode nor re-applies when the
    // user switches the window off mid-session.
    const activateReviewWindowLevel = reviewWindowLevel.activate;
    useEffect(() => {
        activateReviewWindowLevel();
    }, [activateReviewWindowLevel]);

    // ONE state cell for the (model, variant) pair: requireCouchVariant
    // throws at render time on a variant id that does not belong to the
    // model, so a mismatched pair must be unrepresentable — with two
    // independent cells its absence rested on React batching two set
    // calls, not on the state's shape.
    // Starts EMPTY. A pre-filled model is indistinguishable from a chosen
    // one, and the geometry and density overrides differ materially
    // between vendors, so the choice is made visible rather than
    // skippable: nothing is placed, previewed or applied until the user
    // picks a couch.
    const [selection, setSelection] = useState<{
        modelId: CouchModelId;
        variantId: string;
    } | null>(null);
    const modelAndVariant = selection
        ? requireCouchVariant(selection.modelId, selection.variantId)
        : null;
    const model = modelAndVariant?.model ?? null;
    const variant = modelAndVariant?.variant ?? null;

    // Structure names already in the set — what a new couch row's name
    // must not collide with. SUPPORT-typed names are excluded: Apply is
    // only reachable when no SUPPORT structure exists (the one-couch gate
    // below), so a collision with a SUPPORT name can never block a legal
    // Apply, and flagging one would point the user at a name they cannot
    // usefully change.
    const existingNames = useMemo(
        () =>
            readModel
                ? readModel.roiArray
                      .filter(
                          (r) =>
                              r.interpretedType !== RoiInterpretedType.SUPPORT
                      )
                      .map((r) => r.name)
                : [],
        [readModel]
    );

    const hasSupportRois = useSupportRois(readModel).length > 0;

    const { rows, nameErrors, allNamesValid, colors, updateRow } =
        useCouchStructureRows(variant, existingNames);

    const handleApplySuccess = useCallback(() => {
        resetCouchTopRows();
        onClose();
    }, [resetCouchTopRows, onClose]);

    const { isApplying, apply } = useCouchModelApplication({
        environment,
        onSuccess: handleApplySuccess,
        updateView,
    });

    // Closing the panel is blocked mid-apply: the panel narrates a
    // dose-affecting write until it commits or rolls back.
    useReportBusy(isApplying, onPreventCloseChange);

    // A placement is the panel's own working state, so it does not outlive
    // it: an abandoned line cannot be applied on a later visit, nor
    // carried onto a different structure set on the same CT.
    useEffect(() => resetCouchTopRows, [resetCouchTopRows]);

    const handleModelChange = useCallback((event: SelectChangeEvent) => {
        // The value can only be an id this panel rendered into the menu,
        // so a failed lookup is a bug — requireCouchModel throws rather
        // than silently substituting another couch (it also narrows the
        // DOM's bare string to a library id). The variant resets to the
        // new model's first in the SAME state write — a variant id
        // carried over from the previous model would not resolve.
        const nextModel = requireCouchModel(event.target.value);
        setSelection({
            modelId: nextModel.id,
            variantId: nextModel.variants[0].id,
        });
    }, []);

    // Only reachable while a model is selected (the variant select renders
    // under it), so `prev` is never null here.
    const handleVariantChange = useCallback((event: SelectChangeEvent) => {
        setSelection((prev) =>
            prev ? { ...prev, variantId: event.target.value } : prev
        );
    }, []);

    // Seeds the line at a sensible default; the user drags it from there.
    // Offered only while no line exists — once there is one, the line
    // itself is the affordance.
    // Resolved here rather than inside the handler so the indicator below
    // and the button describe the same thing: whatever this picks IS what
    // the reading will use.
    const bodyRoi = useMemo(() => {
        const structureSet = environment?.structureSet;
        // Keyed on the domain set the rest of the panel follows, and read
        // through the engine only while the engine holds that same set. The
        // environment keeps its identity across a structure-set switch, so
        // keying on it alone would keep naming — and reading the freed field
        // of — a structure from the set the user has left.
        if (
            !image ||
            !readModel ||
            !structureSet ||
            structureSet.parent.id !== readModel.id
        ) {
            return null;
        }
        // `texture` rasterises a field for a structure that has never been
        // drawn, which is exactly what a reading of the field needs — so it
        // is resolved only for the candidates `findBodyRoi` asks about.
        // `textureGrid` is the field's own lattice, not the image's.
        return findBodyRoi(
            readModel.roiArray.map((roi) => ({
                name: roi.name,
                interpretedType: roi.interpretedType,
                sdf: () =>
                    structureSdfOf(structureSet.getRoi(roi.roiNumber)?.texture),
            }))
        );
    }, [image, environment, readModel]);
    const readsImage = image !== undefined && !imageGate.isBlocked;

    const handlePlaceLine = useCallback(() => {
        if (!image) {
            return;
        }
        // Read the couch out of what is already on screen, and fall back to
        // the fixed fraction when neither reading finds one. Either way the
        // user lands on a draggable line rather than on an error: a wrong
        // seed costs one drag, which is what they would have done anyway.
        //
        // The pixel read is gated on the same completeness rule Apply uses:
        // `pixelData` throws on a part-volume, and a half-arrived scan would
        // read its unfilled rows as material.
        const found = findCouchTop({
            body: bodyRoi?.sdf ?? null,
            voxels: readsImage ? image.pixelData : null,
            grid: image.grid,
        });

        const voxelY =
            found?.voxelY ??
            INITIAL_PLACEMENT_HEIGHT_FRACTION * image.grid.sizeY;
        setCouchTopRow(image.seriesUid, { voxelY });
        // The fallback is credited to itself rather than to nothing: a
        // line that says where it came from is checkable, and one that
        // differs from a read line only by a missing label is not.
        setFinding(found ?? { voxelY, method: 'default' });
    }, [image, bodyRoi, readsImage, setCouchTopRow]);

    // The last reading, kept so the readout can say which one produced the
    // line the user is looking at. Display only — it never reaches the
    // structures, whose provenance records the library entry the geometry
    // came from and nothing about how the line was found.
    const [finding, setFinding] = useState<CouchTopFinding | null>(null);
    // Claimed only while the line is still WHERE the reading put it. A drag
    // makes the line the user's, and a label still crediting the algorithm
    // for a position the user chose would be worse than no label.
    const placementMethod =
        finding && couchTopRow?.voxelY === finding.voxelY
            ? finding.method
            : null;

    const hasRow = couchTopRow !== null;

    // Feeds the viewport's uncommitted curve preview (`CouchTopLine`).
    // Published as soon as there is both a line and a chosen couch —
    // together those ARE the request to see it, and the ghost is what
    // makes the placement checkable before it is committed. Follows
    // colour edits live; line drags and slice scrolls redraw on the
    // viewport side (CouchTopLine's row/renderTick dependencies) without
    // a republish. Cleared on unmount and whenever either input goes
    // away, so nothing leaves a stale ghost over the CT.
    useEffect(() => {
        if (!hasRow || !variant) {
            setPreviewSelection(null);
            return;
        }
        setPreviewSelection({ variant, structureColors: colors });
        return () => setPreviewSelection(null);
    }, [hasRow, variant, colors, setPreviewSelection]);

    const reviewWindowLevelMessageId = reviewWindowLevel.isActive
        ? 'couch.reviewWindowLevelActiveTooltip'
        : 'couch.reviewWindowLevelTooltip';
    const reviewPreset = requireWindowingPreset(REVIEW_PRESET_NAME);

    // Advisory display value only — not tied to a viewport slice (that is
    // the draggable overlay's job). A row's patient-frame coordinate is
    // rotation-independent of the Z chosen here for every grid this
    // feature can complete on: `placeCouchModel` requires an axis-aligned
    // grid, and an axis-aligned `voxelToWorld` couples no other voxel
    // axis into the row axis, so any Z gives the same answer. The
    // volume's centre slice is used simply as a stable, always-in-range
    // choice. Apply consumes the same voxel Y this readout converts, so
    // what the panel shows is exactly where the shell top gets written.
    //
    // WHICH patient axis this coordinate is on follows the series (see
    // `imageRowWorldAxis`): anterior-posterior for supine and prone,
    // left-right for lateral decubitus. The readout names it, because a
    // bare number whose axis silently changes with the patient's position
    // cannot be checked against a plan.
    const couchTopCoordMm =
        image && couchTopRow
            ? couchTopWorldCoord(
                  couchTopRow.voxelY,
                  image.grid,
                  image.grid.voxelToWorld,
                  image.grid.sizeZ / 2
              )
            : null;
    const couchTopAxis = image
        ? AXIS_LETTERS[imageRowWorldAxis(image.grid.voxelToWorld)]
        : null;

    // One verdict for both halves. The lateral half is knowable the moment
    // a couch is chosen, before any line exists, so the refusal does not
    // wait on a placement that cannot change it; the ghost stays drawn
    // throughout, because the user has to SEE the couch against the image
    // to judge how much larger a FOV they need. Blocking rather than
    // advisory: the image reformat feature can produce a larger FOV, so
    // refusing and naming that remedy is safer than letting a partially
    // modelled couch through.
    const coverage = useMemo(
        () =>
            image && variant
                ? couchCoverage(
                      variant,
                      couchTopRow?.voxelY ?? null,
                      image.grid
                  )
                : null,
        [image, variant, couchTopRow]
    );

    const handleApply = useCallback(() => {
        if (!image || !couchTopRow || !selection || !variant) {
            return;
        }
        void apply({
            image,
            couchModelId: selection.modelId,
            variant,
            couchTopVoxelY: couchTopRow.voxelY,
            rows,
        });
    }, [apply, image, couchTopRow, selection, variant, rows]);

    return (
        <Stack spacing={1}>
            {image && !hasRow && (bodyRoi || readsImage) && (
                <Typography
                    fontSize={10}
                    color="success.main"
                    data-testid="couch-detection-source"
                >
                    {bodyRoi ? (
                        <FormattedMessage
                            id="couch.readsBody"
                            values={{ name: bodyRoi.name }}
                        />
                    ) : (
                        <FormattedMessage id="couch.readsImage" />
                    )}
                </Typography>
            )}

            {image && !hasRow && (
                <DisabledHint
                    title={acquisitionPlaneReason}
                    spanStyle={{ display: 'flex', width: '100%' }}
                    keepWrapper
                >
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={!acquiredSliceShown}
                        onClick={handlePlaceLine}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                        data-testid="couch-place-line"
                    >
                        <FormattedMessage id="couch.placeLine" />
                    </Button>
                </DisabledHint>
            )}

            <ExistingSupportStructuresNotice structureSet={readModel} />

            {/* Visible from the moment a series is loaded: the model
                choices stay in reach whatever else is blocked. Apply is
                gated on a line existing, since without one there is no
                position to place a couch at. */}
            {image && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        {hasRow && couchTopCoordMm !== null ? (
                            <Typography
                                fontSize={12}
                                // The value live-updates during a drag;
                                // tabular digits keep it from jittering
                                // horizontally, same as every other live
                                // readout (BrushToolOptions).
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                                data-testid="couch-top-readout"
                            >
                                <FormattedMessage
                                    id="couch.top"
                                    values={{ axis: couchTopAxis }}
                                />
                                {`: ${formatMmAsCm(couchTopCoordMm)} cm`}
                                {placementMethod && (
                                    <>
                                        {' ('}
                                        <FormattedMessage
                                            id={
                                                COUCH_TOP_METHOD_MESSAGE_ID[
                                                    placementMethod
                                                ]
                                            }
                                        />
                                        {')'}
                                    </>
                                )}
                            </Typography>
                        ) : (
                            // The disabled reason for Preview/Apply below,
                            // in the readout's place — the value that is
                            // missing is exactly what this row shows once
                            // a line exists.
                            <Typography
                                fontSize={12}
                                color="text.secondary"
                                data-testid="couch-no-row-hint"
                            >
                                <FormattedMessage id="couch.noRowHint" />
                            </Typography>
                        )}
                        {/* The tooltip DESCRIBES what pressing this does;
                            the aria-label NAMES the control. Screen readers
                            announce the name on focus, so a two-clause
                            instruction there would be read as the button's
                            identity. */}
                        <Tooltip
                            title={
                                <FormattedMessage
                                    id={reviewWindowLevelMessageId}
                                    values={{
                                        ww: reviewPreset.ww,
                                        wc: reviewPreset.wc,
                                    }}
                                />
                            }
                            placement="bottom"
                        >
                            <IconButton
                                size="small"
                                onClick={reviewWindowLevel.toggle}
                                aria-label={intl.formatMessage({
                                    id: 'couch.reviewWindowLevel',
                                })}
                                aria-pressed={reviewWindowLevel.isActive}
                                sx={(theme) => ({
                                    p: 0.25,
                                    backgroundColor: reviewWindowLevel.isActive
                                        ? alpha(
                                              theme.palette.primary.main,
                                              0.15
                                          )
                                        : undefined,
                                })}
                                data-testid="couch-review-wl"
                            >
                                <Contrast size={14} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box>
                        <Label required>
                            <FormattedMessage id="couch.model" />
                        </Label>
                        <Select
                            value={selection?.modelId ?? ''}
                            displayEmpty
                            renderValue={(modelId) =>
                                modelId ? (
                                    requireCouchModel(modelId).name
                                ) : (
                                    <Typography
                                        component="span"
                                        fontSize={12}
                                        color="text.secondary"
                                    >
                                        <FormattedMessage id="couch.modelPlaceholder" />
                                    </Typography>
                                )
                            }
                            onChange={handleModelChange}
                            size="small"
                            fullWidth
                            MenuProps={menuProps}
                            data-testid="couch-model-select"
                        >
                            {COUCH_MODELS.map((model) => (
                                <MenuItem
                                    key={model.id}
                                    value={model.id}
                                    sx={{ fontSize: 12 }}
                                >
                                    {model.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {model && model.variants.length > 1 && (
                        <Box>
                            <Label>
                                <FormattedMessage id="couch.variant" />
                            </Label>
                            <Select
                                value={selection?.variantId ?? ''}
                                onChange={handleVariantChange}
                                size="small"
                                fullWidth
                                MenuProps={menuProps}
                                data-testid="couch-variant-select"
                            >
                                {model.variants.map((v) => (
                                    <MenuItem
                                        key={v.id}
                                        value={v.id}
                                        sx={{ fontSize: 12 }}
                                    >
                                        {intl.formatMessage({
                                            id: requireVariantLabelId(v),
                                        })}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    <Stack spacing={1}>
                        <Label>
                            <FormattedMessage id="couch.structures" />
                        </Label>
                        {/* Rows align 1:1 with the components by
                            construction: the rows hook stores them with
                            the variant they were seeded for and replaces
                            both together — no fallback values needed. */}
                        {variant?.components.map((component, index) => (
                            <CouchStructureRow
                                key={`${variant.id}-${index}`}
                                roiName={component.roiName}
                                color={rows[index].color}
                                onColorChange={(color) =>
                                    updateRow(index, { color })
                                }
                                name={rows[index].name}
                                onNameChange={(name) =>
                                    updateRow(index, { name })
                                }
                                nameError={nameErrors[index] ?? null}
                                relativeDensity={component.density.relElectron}
                                dataTestId={`couch-structure-row-${index}`}
                            />
                        ))}
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    {/* The headline names the remedy (the same
                        problem-then-remedy idiom as `tool.disabled.oblique`);
                        the per-direction lines below say WHICH side and BY
                        HOW MUCH, each rendered only when that side actually
                        overhangs. Top cannot for a couch top inside the
                        image — every library loop extends downward — but it
                        is computed rather than assumed away, because the
                        line can be dragged above a short image's top edge. */}
                    {coverage?.isBlocked && (
                        <Typography
                            fontSize={10}
                            color="error"
                            data-testid="couch-coverage-blocked"
                        >
                            <FormattedMessage id="couch.coverageBlocked" />
                        </Typography>
                    )}
                    {coverage && coverage.lateralMm > 0 && (
                        <Typography
                            fontSize={10}
                            color="error"
                            data-testid="couch-coverage-lateral"
                        >
                            <FormattedMessage
                                id="couch.coverageLateral"
                                values={{
                                    cm: formatMmAsCm(coverage.lateralMm),
                                }}
                            />
                        </Typography>
                    )}
                    {coverage && coverage.belowMm > 0 && (
                        <Typography
                            fontSize={10}
                            color="error"
                            data-testid="couch-coverage-below"
                        >
                            <FormattedMessage
                                id="couch.coverageBelow"
                                values={{ cm: formatMmAsCm(coverage.belowMm) }}
                            />
                        </Typography>
                    )}
                    {coverage && coverage.aboveMm > 0 && (
                        <Typography
                            fontSize={10}
                            color="error"
                            data-testid="couch-coverage-above"
                        >
                            <FormattedMessage
                                id="couch.coverageAbove"
                                values={{ cm: formatMmAsCm(coverage.aboveMm) }}
                            />
                        </Typography>
                    )}

                    {/* The ghost draws itself from the line and the chosen
                        couch, so the only button here writes. Apply needs
                        the names valid, the one-couch gate clear, full FOV
                        coverage, the whole image loaded and a tile showing
                        an acquired slice of it; the ghost stays drawn
                        regardless, so the user can see the couch against
                        the image and judge how much larger a FOV they need
                        before reformatting. While an apply runs, the
                        button's own label says why it is disabled. */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <ActionButtons
                                label={intl.formatMessage({
                                    id: isApplying
                                        ? 'couch.applying'
                                        : 'couch.apply',
                                })}
                                onApply={handleApply}
                                isApplyDisabled={
                                    !variant ||
                                    !hasRow ||
                                    !allNamesValid ||
                                    isApplying ||
                                    hasSupportRois ||
                                    coverage?.isBlocked ||
                                    imageGate.isBlocked ||
                                    !acquiredSliceShown
                                }
                                applyDisabledReason={
                                    isApplying
                                        ? undefined
                                        : (imageGate.reason ??
                                          acquisitionPlaneReason)
                                }
                                startIcon={
                                    isApplying ? (
                                        <CircularProgress
                                            size={14}
                                            color="inherit"
                                        />
                                    ) : undefined
                                }
                                dataTestId="couch-apply"
                            />
                        </Box>
                    </Box>
                </>
            )}
        </Stack>
    );
}
